import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { SelectOption } from 'primitives/select'
import { useCallback, useMemo, useState } from 'react'
import { useDebounce } from './use-debounce'

export interface UseRemoteSelectOptions {
  queryKey: unknown[]
  fetchService: (params: Record<string, unknown>) => Promise<{ data: unknown }>
  optionExtractor: (data: unknown) => {
    options: SelectOption[]
    hasNextPage?: boolean
    nextPage?: number
  }
  searchKey?: string
  pageSize?: number
  initialPage?: number
  enabled?: boolean
  selectedValue?: string | number
  fetchResource?: (id: string | number) => Promise<{ data: unknown }>
  optionFromResource?: (data: unknown) => SelectOption
  enableOffsetMode?: boolean
}

export interface UseRemoteSelectReturn {
  options: SelectOption[]
  isLoading: boolean
  error: Error | null
  searchTerm: string
  setSearchTerm: (term: string) => void
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  loadMore: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

const isValueInOptions = (value: string | number, options: SelectOption[]): boolean => {
  const normalized = String(value)
  return options.some((option) => String(option.value) === normalized)
}

export function useRemoteSelect({
  queryKey,
  fetchService,
  optionExtractor,
  searchKey = 'search',
  pageSize = 20,
  initialPage = 1,
  enabled = true,
  selectedValue,
  fetchResource,
  optionFromResource,
  enableOffsetMode = false,
}: UseRemoteSelectOptions): UseRemoteSelectReturn {
  if (selectedValue && (!fetchResource || !optionFromResource)) {
    throw new Error(
      'fetchResource and optionFromResource are required when selectedValue is provided',
    )
  }

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [isOpen, setIsOpen] = useState(false)
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  // Infinite query for fetching options
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } =
    useInfiniteQuery({
      queryKey: [...queryKey, debouncedSearchTerm, initialPage, pageSize, enableOffsetMode],
      queryFn: async ({ pageParam = initialPage }) => {
        const params: Record<string, unknown> = {
          page: pageParam,
          limit: pageSize,
        }

        if (enableOffsetMode) {
          params.offset = (pageParam - 1) * pageSize
        }

        if (debouncedSearchTerm.trim()) {
          params[searchKey] = debouncedSearchTerm.trim()
        }

        const response = await fetchService(params)
        return response.data
      },
      initialPageParam: initialPage,
      getNextPageParam: (lastPage: unknown, allPages: unknown[]) => {
        try {
          const extracted = optionExtractor(lastPage)
          return extracted.hasNextPage ? extracted.nextPage || allPages.length + 1 : undefined
        } catch (_err) {
          return undefined
        }
      },
      enabled, // Control query execution based on the provided flag
    })

  // Extract and flatten all options from pages
  const options = useMemo(() => {
    if (!data?.pages) return []

    return data.pages.flatMap((page: unknown) => {
      try {
        const extracted = optionExtractor(page)
        return extracted.options || []
      } catch (_err) {
        return []
      }
    })
  }, [data?.pages, optionExtractor])

  // Check if selected value exists in current options
  const selectedValueExists = useMemo(() => {
    if (!selectedValue) return true
    return isValueInOptions(selectedValue, options)
  }, [options, selectedValue])

  // Fetch selected resource if it's not in current options
  const { data: selectedResourceData, isLoading: isLoadingSelectedResource } = useQuery({
    queryKey: [...queryKey, 'selected-resource', selectedValue],
    queryFn: async () => {
      const response = await fetchResource!(selectedValue!)
      return response.data
    },
    enabled: Boolean(fetchResource && selectedValue && !selectedValueExists && enabled),
  })

  // Extract option from fetched resource
  const selectedOption = useMemo(() => {
    if (!selectedResourceData || !optionFromResource) return null
    try {
      return optionFromResource(selectedResourceData)
    } catch {
      return null
    }
  }, [selectedResourceData, optionFromResource])

  // Handle load more when scrolling
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Merge selected option into options if it was fetched
  const optionsWithSelected = useMemo(() => {
    if (!selectedOption) return options
    if (isValueInOptions(selectedOption.value, options)) return options

    // Add selected option at the beginning
    return [selectedOption, ...options]
  }, [options, selectedOption])

  // Add loading indicator to options if fetching more
  const optionsWithLoading = useMemo(() => {
    const allOptions = [...optionsWithSelected]
    if (isFetchingNextPage) {
      allOptions.push({
        value: '__loading__',
        label: 'Loading more...',
        disabled: true,
      })
    }
    return allOptions
  }, [optionsWithSelected, isFetchingNextPage])

  return {
    options: optionsWithLoading,
    isLoading: isLoading || isLoadingSelectedResource,
    error,
    searchTerm,
    setSearchTerm,
    isOpen,
    setIsOpen,
    loadMore,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  }
}
